<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">HOSPITAL BILLING</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="javascript:void(0);" @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item active">Billing</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted mb-0">Loading billing information...</p>
            </div>

            <div v-else class="row">
              <!-- Left Column: Patient Info & Add Charge Form -->
              <div class="col-lg-5">
                <!-- Patient Card -->
                <div class="card">
                  <div class="card-body p-4 bg-light">
                    <div class="d-flex justify-content-between align-items-center mb-3">
                      <p class="mb-0 fw-semibold text-uppercase">Patient Details</p>
                    </div>
                    <hr>
                    <div class="row">
                      <div class="col-4">
                        <div class="d-flex">
                          <div class="flex-shrink-0 me-3 align-self-center">
                            <i class="mdi mdi-account h2 text-dark"></i>
                          </div>
                          <div class="flex-grow-1">
                            <p class="text-muted mb-0">Patient Name</p>
                            <h5 class="mb-0 font-14px">{{ visit.patientName }}</h5>
                          </div>
                        </div>
                      </div>
                      <div class="col-4">
                        <div class="d-flex">
                          <div class="flex-shrink-0 me-3 align-self-center">
                            <i class="mdi mdi-phone h2 text-dark"></i>
                          </div>
                          <div class="flex-grow-1">
                            <p class="text-muted mb-0">Phone No.</p>
                            <h5 class="mb-0 font-14px">{{ visit.phoneNumber || '-' }}</h5>
                          </div>
                        </div>
                      </div>
                      <div class="col-4">
                        <div class="d-flex">
                          <div class="flex-shrink-0 me-3 align-self-center">
                            <i class="mdi mdi-clipboard-text h2 text-dark"></i>
                          </div>
                          <div class="flex-grow-1">
                            <p class="text-muted mb-0">Visit No.</p>
                            <h5 class="mb-0 font-14px">{{ visit.visitNo }}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Add Charge Form -->
                <div class="card">
                  <div class="card-body p-4">
                    <h4 class="card-title mb-4">Add Service Charge</h4>

                    <div v-if="message" class="alert" :class="messageType === 'error' ? 'alert-danger' : 'alert-info'" role="alert">
                      {{ message }}
                    </div>

                    <form>
                      <!-- Income Type -->
                      <div class="row">
                        <div class="col-12">
                          <div class="mb-3">
                            <label class="form-label">Income Type <span class="text-danger">*</span></label>
                            <select class="form-select" v-model="selectedIncomeType" @change="onIncomeTypeChange">
                              <option value="">-- Select Income Type --</option>
                              <option v-for="(item, index) in incomeTypes" :key="index" :value="JSON.stringify(item)">
                                {{ item.incomeTypeDescription }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <!-- Fee/Charge -->
                      <div class="row">
                        <div class="col-12">
                          <div class="mb-3">
                            <label class="form-label">Fee/Charge <span class="text-danger">*</span></label>
                            <select class="form-select" v-model="selectedFee" @change="onFeeChange" :disabled="!feesAndCharges.length">
                              <option value="">-- Select Fee --</option>
                              <option v-for="(item, index) in feesAndCharges" :key="index" :value="JSON.stringify(item)">
                                {{ item.feeDescription }} - KES {{ numberFormat(item.unitFeeAmount) }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <!-- Sub County & Ward -->
                      <div class="row">
                        <div class="col-6">
                          <div class="mb-3">
                            <label class="form-label">Sub County</label>
                            <select class="form-select" v-model="subCounty" @change="onSubCountyChange">
                              <option value="">-- Select Sub County --</option>
                              <option v-for="(item, index) in subCounties" :key="index" :value="JSON.stringify(item)">
                                {{ item.subCountyName }}
                              </option>
                            </select>
                          </div>
                        </div>
                        <div class="col-6">
                          <div class="mb-3">
                            <label class="form-label">Ward</label>
                            <select class="form-select" v-model="ward" :disabled="!wards.length">
                              <option value="">-- Select Ward --</option>
                              <option v-for="(item, index) in wards" :key="index" :value="JSON.stringify(item)">
                                {{ item.wardName }}
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <!-- Description -->
                      <div class="mb-3">
                        <label class="form-label">Details/Description</label>
                        <textarea class="form-control" rows="2" v-model="description" placeholder="Additional details..."></textarea>
                      </div>

                      <!-- Quantity & Amount -->
                      <div class="row">
                        <div class="col-4">
                          <div class="mb-3">
                            <label class="form-label">Quantity</label>
                            <input type="number" class="form-control" v-model="quantity" min="1">
                          </div>
                        </div>
                        <div class="col-4">
                          <div class="mb-3">
                            <label class="form-label">Unit Price</label>
                            <input type="text" class="form-control" :value="numberFormat(unitFeeAmount)" readonly>
                          </div>
                        </div>
                        <div class="col-4">
                          <div class="mb-3">
                            <label class="form-label">Total</label>
                            <input type="text" class="form-control bg-light fw-bold" :value="'KES ' + numberFormat(totalItemAmount)" readonly>
                          </div>
                        </div>
                      </div>

                      <button @click="addToBill" type="button" class="btn btn-primary waves-effect btn-label waves-light" :disabled="!selectedFeeData">
                        <i class="bx bx-plus label-icon"></i> Add to Bill
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              <!-- Right Column: Bill Items & Generate -->
              <div class="col-lg-7">
                <div class="card">
                  <div class="card-body p-4">
                    <h4 class="card-title mb-4">Bill Items</h4>

                    <!-- Bill Items Table -->
                    <div class="table-responsive border">
                      <table class="table table-nowrap align-middle table-sm mb-0">
                        <thead class="bg-light">
                        <tr>
                          <th style="width: 50px;">No.</th>
                          <th>Description</th>
                          <th class="text-end">Amount</th>
                          <th style="width: 80px;"></th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(item, index) in billItems" :key="index">
                          <td>{{ index + 1 }}</td>
                          <td>
                            <p class="mb-0">{{ item.feeDescription }}</p>
                            <small class="text-muted" v-if="item.description">{{ item.description }}</small>
                          </td>
                          <td class="text-end">KES {{ numberFormat(item.unitFeeAmount) }} (x {{ item.quantity }})</td>
                          <td class="text-end">
                            <button @click="removeFromBill(index)" type="button" class="btn btn-link text-danger p-0">
                              <i class="mdi mdi-close font-size-18"></i>
                            </button>
                          </td>
                        </tr>
                        <tr v-if="billItems.length === 0">
                          <td colspan="4" class="text-center text-muted py-4">
                            <i class="mdi mdi-receipt font-size-24 d-block mb-2"></i>
                            No items added yet
                          </td>
                        </tr>
                        </tbody>
                        <tfoot class="bg-light">
                        <tr>
                          <td colspan="2" class="text-end fw-bold">TOTAL:</td>
                          <td class="text-end">
                            <h5 class="mb-0 text-primary fw-bold">KES {{ numberFormat(totalAmount) }}</h5>
                          </td>
                          <td></td>
                        </tr>
                        </tfoot>
                      </table>
                    </div>

                    <!-- Pay Later Toggle -->
                    <div class="mb-3 mt-4 p-3 bg-light rounded">
                      <div class="form-check form-switch">
                        <input class="form-check-input" type="checkbox" id="payLaterSwitch" v-model="payLater" style="width: 3rem; height: 1.5rem;">
                        <label class="form-check-label ms-2 pt-1" for="payLaterSwitch">
                            <span v-if="!payLater" class="fw-semibold text-success">
                              <i class="mdi mdi-cash-fast me-1"></i> Pay Now
                            </span>
                          <span v-else class="fw-semibold text-warning">
                              <i class="mdi mdi-clock-outline me-1"></i> Pay Later
                              <small class="text-muted ms-2">(SMS & Email will be sent to customer)</small>
                            </span>
                        </label>
                      </div>
                    </div>

                    <!-- Generate Bill Button -->
                    <button @click="generateBill" type="button" class="btn btn-success btn-lg waves-effect btn-label waves-light" :disabled="billItems.length === 0 || generatingBill">
                        <span v-if="generatingBill">
                          <i class="mdi mdi-loading mdi-spin label-icon"></i> Generating...
                        </span>
                      <span v-else>
                          <i class="bx bx-check label-icon"></i> Generate Bill
                        </span>
                    </button>
                  </div>
                </div>

                <!-- Previous Visit Charges (if any) -->
                <div v-if="existingCharges.length > 0" class="card">
                  <div class="card-body p-4">
                    <h5 class="mb-3"><i class="mdi mdi-history me-2 text-primary"></i>Previous Charges</h5>
                    <div class="table-responsive">
                      <table class="table table-sm">
                        <thead class="table-light">
                        <tr>
                          <th>Service</th>
                          <th class="text-end">Amount</th>
                          <th>Status</th>
                          <th>Bill No</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(charge, idx) in existingCharges" :key="idx">
                          <td>{{ charge.serviceName }}</td>
                          <td class="text-end">KES {{ numberFormat(charge.totalAmount) }}</td>
                          <td>
                                <span class="badge" :class="charge.chargeStatus === 'PAID' ? 'bg-success' : 'bg-warning'">
                                  {{ charge.chargeStatus || 'PENDING' }}
                                </span>
                          </td>
                          <td><small class="text-primary">{{ charge.billNo || '-' }}</small></td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

    <!-- Bill Generated Modal -->
    <div class="modal fade" id="billGeneratedModal" tabindex="-1">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header bg-success text-white">
            <div class="d-flex align-items-center">
              <i class="mdi mdi-check-circle font-size-32 me-3"></i>
              <div>
                <h5 class="modal-title mb-0">Bill Generated Successfully</h5>
                <small class="opacity-75">Your invoice has been created</small>
              </div>
            </div>
            <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body p-4">
            <!-- Pay Later Notification -->
            <div v-if="payLater" class="alert alert-info d-flex align-items-center mb-4">
              <i class="mdi mdi-send-check font-size-24 me-3"></i>
              <div>
                <h6 class="mb-1">Invoice Sent to Customer</h6>
                <p class="mb-0 font-size-13">
                  <span v-if="visit.phoneNumber"><i class="mdi mdi-cellphone me-1"></i> SMS sent to {{ visit.phoneNumber }}</span>
                </p>
              </div>
            </div>

            <!-- Bill Details -->
            <div class="card border mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0"><i class="mdi mdi-receipt me-2"></i>Invoice Information</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="text-muted mb-1">Invoice Number:</label>
                    <h5 class="mb-0 text-primary fw-bold">{{ billDetails.billNo }}</h5>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="text-muted mb-1">Total Amount:</label>
                    <h5 class="mb-0 text-success fw-bold">KES {{ numberFormat(billDetails.detailAmount) }}</h5>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="text-muted mb-1">Status:</label>
                    <span class="badge bg-warning font-size-14 px-3 py-2">{{ billDetails.status }}</span>
                  </div>
                </div>
                <hr>
                <div class="row">
                  <div class="col-md-6">
                    <label class="text-muted mb-1">Patient:</label>
                    <div class="fw-semibold">{{ billDetails.customerName }}</div>
                  </div>
                  <div class="col-md-6">
                    <label class="text-muted mb-1">Visit No:</label>
                    <div class="fw-semibold">{{ visit.visitNo }}</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Bill Items -->
            <div class="card border mb-4">
              <div class="card-header bg-light">
                <h6 class="mb-0"><i class="mdi mdi-format-list-bulleted me-2"></i>Invoice Items</h6>
              </div>
              <div class="card-body p-0">
                <table class="table mb-0">
                  <thead class="table-light">
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th class="text-center">Qty</th>
                    <th class="text-end">Total</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(item, index) in billInfo" :key="index">
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.feeDescription }}</td>
                    <td class="text-center">{{ item.quantity || 1 }}</td>
                    <td class="text-end fw-bold">KES {{ numberFormat(item.billTotal) }}</td>
                  </tr>
                  </tbody>
                  <tfoot class="table-light">
                  <tr>
                    <td colspan="3" class="text-end fw-bold">TOTAL:</td>
                    <td class="text-end"><h5 class="mb-0 text-success fw-bold">KES {{ numberFormat(billDetails.detailAmount) }}</h5></td>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            <!-- Payment Information -->
            <div v-if="billDetails.printable === 'true'" class="card border-primary mb-0">
              <div class="card-header bg-primary bg-opacity-10">
                <h6 class="mb-0 text-primary"><i class="mdi mdi-cash-multiple me-2"></i>Payment Information</h6>
              </div>
              <div class="card-body">
                <div class="row">
                  <div class="col-md-6">
                    <h6 class="fw-semibold mb-3"><i class="mdi mdi-cellphone me-2"></i>M-PESA Payment</h6>
                    <div class="bg-light p-3 rounded">
                      <div class="mb-2">
                        <small class="text-muted">Paybill Number:</small>
                        <h5 class="mb-0 fw-bold">{{ payBill.shortCode }}</h5>
                      </div>
                      <div class="mb-2">
                        <small class="text-muted">Account Number:</small>
                        <code class="fw-bold">{{ billDetails.billNo }}</code>
                      </div>
                      <div>
                        <small class="text-muted">Amount:</small>
                        <h6 class="mb-0 text-success fw-bold">KES {{ numberFormat(billDetails.billBalance) }}</h6>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <h6 class="fw-semibold mb-3"><i class="mdi mdi-bank me-2"></i>Bank Payment</h6>
                    <div class="bg-light p-3 rounded">
                      <div class="mb-2">
                        <small class="text-muted">Bank Name:</small>
                        <div class="fw-semibold">{{ bank.bank }}</div>
                      </div>
                      <div class="mb-2">
                        <small class="text-muted">Account Number:</small>
                        <code class="fw-bold">{{ bank.payBill }}</code>
                      </div>
                      <div>
                        <small class="text-muted">Reference:</small>
                        <code>{{ billDetails.billNo }}</code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer bg-light">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              <i class="mdi mdi-close me-1"></i> Close
            </button>
            <button v-if="billDetails.printable === 'true'" @click="printBill" type="button" class="btn btn-info">
              <i class="mdi mdi-printer me-1"></i> Print Invoice
            </button>
            <button v-if="billDetails.printable === 'true'" @click="proceedToPayment" type="button" class="btn btn-success">
              <i class="mdi mdi-credit-card me-1"></i> Proceed to Payment
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <ToastNotification ref="toast" />

    <!-- Payment Modal -->
    <PaymentModal
        :message="paymentMessage"
        :payment-panel="paymentPanel"
        :payment-method="paymentMethod"
        :transaction-summary="transactionSummary"
        :transaction-completed="transactionCompleted"
        v-model:phone-number="customerPhone"
        :bill-details="billDetails"
        :payment-data="paymentData"
        :receipt-details="receiptDetails"
        :alert="alert"
        @show-payment-method="showPaymentMethod"
        @show-transaction-summary="showTransactionSummary"
        @make-payment="makePayment"
        @print-bill="printBill"
        @print-receipt="printReceipt"
        @update:bankReceiptNo="paymentData.bankReceiptNo = $event"
    />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import PaymentModal from "@/components/modals/BillModals/PaymentModal.vue";
import { execute, hospital, biller } from "@/api";

export default {
  name: "Billing",
  components: { NavigationBar, ToastNotification, PaymentModal },
  data() {
    return {
      loading: true,
      visitID: '',
      visit: {},
      existingCharges: [],

      // Income Types & Fees (from countyBiller)
      incomeTypes: [],
      feesAndCharges: [],
      selectedIncomeType: '',
      selectedFee: '',
      selectedFeeData: null,

      // Location
      subCounties: [],
      wards: [],
      subCounty: '',
      ward: '',

      // Charge details
      description: '',
      quantity: 1,
      unitFeeAmount: 0,
      feeID: '',

      // Bill Items
      billItems: [],
      totalAmount: 0,

      // Bill Generation
      payLater: false,
      generatingBill: false,
      message: null,
      messageType: 'info',

      // Generated Bill Details
      billDetails: {
        id: '',
        billNo: '',
        incomeTypeID: '',
        incomeTypeDescription: '',
        costCenterNo: '',
        accountNo: '',
        feeID: '',
        detailAmount: '',
        receiptAmount: '',
        billBalance: '',
        wardID: '',
        subCountyID: '',
        status: '',
        dateCreated: '',
        printable: 'false',
        customerName: '',
        customerPhoneNumber: '',
        subCountyName: '',
        wardName: '',
        names: '',
        description: ''
      },
      billInfo: [],
      bank: {
        id: null,
        bankID: null,
        bank: '',
        payBill: '',
        format: '',
        bankType: '',
        accountNo: null,
        company: ''
      },
      payBill: {
        id: null,
        stream: '',
        shortCode: ''
      },
      county: {},

      // Payment Modal State
      paymentPanel: true,
      paymentMethod: false,
      transactionSummary: false,
      transactionCompleted: false,
      paymentMessage: null,
      customerPhone: '',

      // Payment Data
      paymentData: {
        bankReceiptNo: null,
        accNo: null,
        payBillNo: null,
        amount: null,
        phoneNumber: null,
        success: false,
        paymentMode: null,
        sessionNames: null,
        sessionIdNo: null,
        sessionPhoneNumber: null
      },

      // Receipt Details
      receiptDetails: {
        id: '',
        receiptNo: null,
        billNo: '',
        detailAmount: '',
        receiptAmount: '',
        billBalance: '',
        costCenterNo: '',
        accountNo: '',
        incomeTypeDescription: '',
        feeID: '',
        wardID: '',
        subCountyID: '',
        currency: '',
        source: '',
        transactionCode: '',
        paidBy: '',
        dateCreated: '',
        dateModified: '',
        createdBy: '',
        modifiedBy: '',
        isActive: '',
        status: ''
      },

      // Alert state for bank receipting
      alert: {
        success: false,
        info: false,
        failed: false
      },

      // Payment check timer
      timer: null,
      paymentCheckStartTime: null,
      paymentTimeoutDuration: 35000,
      isCheckingPayment: false,

      // Reconciled transactions for bank payment
      ReconTransactions: [],

      // Session
      facilityID: '',
      facilityName: '',
      sessionNames: '',
      sessionIdNo: '',
      zone: ''
    };
  },
  computed: {
    totalItemAmount() {
      return (this.quantity || 1) * (this.unitFeeAmount || 0);
    }
  },
  watch: {
    quantity() {
      // Recalculate when quantity changes
    },
    subCounty(newVal) {
      if (newVal) {
        try {
          const subCountyData = JSON.parse(newVal);
          this.getWards(subCountyData.subCountyID);
        } catch (e) {
          console.error('Error parsing subCounty:', e);
        }
      } else {
        this.wards = [];
        this.ward = '';
      }
    },
    'paymentData.bankReceiptNo': function(newVal) {
      if (newVal !== '' && newVal !== null) {
        this.getReconciledRecords(newVal);
      }
    }
  },
  beforeUnmount() {
    this.stopPaymentCheck();
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    showMessage(msg, type = 'info') {
      this.message = msg;
      this.messageType = type;
      if (type !== 'error') {
        setTimeout(() => { this.message = null; }, 5000);
      }
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    },

    // Load Visit Details
    loadVisit() {
      const data = new FormData();
      data.append("function", "getVisit");
      data.append("visitID", this.visitID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.visit = res.data.data.visit || {};
            }
          })
          .catch((e) => console.error(e));
    },

    // Load existing charges for this visit
    loadExistingCharges() {
      const data = new FormData();
      data.append("function", "getVisitCharges");
      data.append("visitID", this.visitID);

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.existingCharges = res.data.data.charges || [];
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error(e);
          });
    },

    // Load Income Types from countyBiller
    getIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      // Filter for medical/health services if needed
      // data.append("incomeTypePrefix", "MED");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.incomeTypes = res.data.data.incomeTypes || [];
            } else {
              console.error('Failed to load income types:', res.data.message);
            }
          })
          .catch((e) => {
            console.error('Error loading income types:', e);
          });
    },

    // Handle Income Type Selection
    onIncomeTypeChange() {
      this.feesAndCharges = [];
      this.selectedFee = '';
      this.selectedFeeData = null;
      this.unitFeeAmount = 0;

      if (this.selectedIncomeType) {
        try {
          const incomeType = JSON.parse(this.selectedIncomeType);
          this.getFeesAndCharges(incomeType.incomeTypeId);
        } catch (e) {
          console.error('Error parsing income type:', e);
        }
      }
    },

    // Load Fees and Charges from countyBiller
    getFeesAndCharges(incomeTypeId) {
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", incomeTypeId);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.feesAndCharges = res.data.data.feesAndCharges || [];
            } else {
              this.showMessage(res.data.message, 'error');
            }
          })
          .catch((e) => {
            this.showMessage(e.message, 'error');
          });
    },

    // Handle Fee Selection
    onFeeChange() {
      if (this.selectedFee) {
        try {
          this.selectedFeeData = JSON.parse(this.selectedFee);
          this.feeID = this.selectedFeeData.feeId;
          this.unitFeeAmount = parseFloat(this.selectedFeeData.unitFeeAmount) || 0;
        } catch (e) {
          console.error('Error parsing fee:', e);
        }
      } else {
        this.selectedFeeData = null;
        this.unitFeeAmount = 0;
      }
    },

    // Load Sub Counties
    getSubCounties() {
      const data = new FormData();
      data.append("function", "getSubCounty");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.subCounties = res.data.data.subCounties || [];
            }
          })
          .catch((e) => console.error(e));
    },

    // Handle Sub County Change
    onSubCountyChange() {
      this.ward = '';
      this.wards = [];
      if (this.subCounty) {
        try {
          const subCountyData = JSON.parse(this.subCounty);
          this.getWards(subCountyData.subCountyID);
        } catch (e) {
          console.error('Error parsing subCounty:', e);
        }
      }
    },

    // Load Wards
    getWards(subCountyID) {
      if (!subCountyID) return;

      const data = new FormData();
      data.append("function", "getWards");
      data.append("subCountyID", subCountyID);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.wards = res.data.data.wards || [];
            }
          })
          .catch((e) => console.error(e));
    },

    // Add item to bill
    addToBill() {
      if (!this.selectedFeeData) {
        this.showMessage('Please select a fee/charge', 'error');
        return;
      }

      // Parse location data
      let subCountyData = { subCountyID: '', subCountyName: '' };
      let wardData = { wardID: '', wardName: '' };

      try {
        if (this.subCounty) subCountyData = JSON.parse(this.subCounty);
        if (this.ward) wardData = JSON.parse(this.ward);
      } catch (e) {
        console.error('Error parsing location data:', e);
      }

      const billItem = {
        fiscalYear: new Date().getFullYear().toString(),
        typeDescription: this.selectedFeeData.typeDescription || '',
        feeDescription: this.selectedFeeData.feeDescription,
        quantity: this.quantity || 1,
        unitFeeAmount: this.unitFeeAmount,
        accountNo: this.selectedFeeData.accountNo,
        costCenterNo: this.selectedFeeData.costCenterNo,
        incomeTypeId: this.selectedFeeData.incomeTypeId,
        revenueStreamItem: this.visit.patientName,
        feeId: this.selectedFeeData.feeId,
        amount: this.totalItemAmount,
        customer: 'Visit: ' + this.visit.visitNo,
        zone: this.zone,
        subCountyID: subCountyData.subCountyID,
        subCountyName: subCountyData.subCountyName,
        wardID: wardData.wardID,
        wardName: wardData.wardName,
        idNo: this.sessionIdNo,
        phoneNumber: sessionStorage.getItem("sessionPhoneNumber"),
        names: this.sessionNames,
        customerPhoneNumber: this.visit.phoneNumber || '',
        description: this.description,
        customerName: this.visit.patientName,
        // Hospital specific
        visitID: this.visitID,
        visitNo: this.visit.visitNo,
        patientID: this.visit.patientID
      };

      this.billItems.push(billItem);
      this.totalAmount = this.billItems.reduce((sum, item) => sum + (item.unitFeeAmount * item.quantity), 0);

      // Reset form
      this.selectedFee = '';
      this.selectedFeeData = null;
      this.unitFeeAmount = 0;
      this.quantity = 1;
      this.description = '';

      this.showMessage('Item added to bill');
    },

    // Remove item from bill
    removeFromBill(index) {
      this.billItems.splice(index, 1);
      this.totalAmount = this.billItems.reduce((sum, item) => sum + (item.unitFeeAmount * item.quantity), 0);
    },

    // Generate Bill (same as BillNew.vue)
    generateBill() {
      if (this.billItems.length === 0) {
        this.showMessage('Please add at least one item to the bill', 'error');
        return;
      }

      this.generatingBill = true;
      this.showMessage('Generating Bill...please wait...');

      const data = new FormData();
      data.append("function", "generateBill3");
      data.append("penalized", false);
      data.append("billItem", JSON.stringify(this.billItems));
      data.append("payNow", this.payLater ? "false" : "true");

      if (this.payLater) {
        data.append("customerPhoneNumber", this.visit.phoneNumber || "");
        data.append("email", this.visit.email || "");
      }

      execute(data, biller)
          .then((res) => {
            this.generatingBill = false;

            if (res.data.success) {
              this.billDetails = res.data.data.bill.billDetails || {};
              this.billInfo = res.data.data.bill.billInfo || [];
              this.bank = res.data.data.bill.bank || {};
              this.payBill = res.data.data.bill.payBill || {};
              this.county = res.data.data.bill.county || {};

              // Setup payment data
              this.paymentData.amount = this.billDetails.billBalance;
              this.paymentData.accNo = this.billDetails.billNo;
              this.paymentData.payBillNo = res.data.data.billGenerated?.payBillNo || this.payBill.shortCode;
              this.paymentData.sessionPhoneNumber = sessionStorage.getItem("sessionPhoneNumber");
              this.paymentData.sessionNames = this.sessionNames;
              this.paymentData.sessionIdNo = this.sessionIdNo;

              // Set customer phone for payment
              this.customerPhone = this.visit.phoneNumber || '';

              this.showMessage('Bill generated successfully!');

              // Also save charge to hospital visit_charges table
              this.saveVisitCharges();

              // Show success modal
              const modal = new bootstrap.Modal(document.getElementById('billGeneratedModal'));
              modal.show();

              // Clear bill items
              this.billItems = [];
              this.totalAmount = 0;

            } else {
              this.showMessage(res.data.message || 'Failed to generate bill', 'error');
            }
          })
          .catch((e) => {
            this.generatingBill = false;
            this.showMessage('Error: ' + e.message, 'error');
          });
    },

    // Save charges to hospital visit_charges table for tracking
    saveVisitCharges() {
      // Save each bill item to hospital visit_charges for local tracking
      this.billInfo.forEach(item => {
        const data = new FormData();
        data.append("function", "addVisitCharge");
        data.append("facilityID", this.facilityID);
        data.append("facilityName", this.facilityName);
        data.append("visitID", this.visitID);
        data.append("visitNo", this.visit.visitNo);
        data.append("patientID", this.visit.patientID);
        data.append("patientNo", this.visit.patientID);
        data.append("serviceChargeID", item.feeID || '');
        data.append("serviceName", item.feeDescription);
        data.append("serviceType", item.incomeTypeDescription || 'Medical');
        data.append("quantity", item.quantity || 1);
        data.append("unitAmount", item.unitFeeAmount || item.billTotal);
        data.append("billNo", this.billDetails.billNo);
        data.append("createdBy", this.sessionNames);
        data.append("sessionNames", this.sessionNames);
        data.append("sessionIdNo", this.sessionIdNo);

        execute(data, hospital).catch(e => console.error('Error saving visit charge:', e));
      });

      // Reload existing charges
      setTimeout(() => this.loadExistingCharges(), 1000);
    },

    // Print Bill
    printBill() {
      if (this.billDetails.printable === 'false') {
        alert('This invoice must be approved before it can be printed.');
        return;
      }

      localStorage['params'] = JSON.stringify({
        billNo: this.billDetails.billNo
      });

      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    // Proceed to Payment - Opens Payment Modal
    proceedToPayment() {
      if (this.billDetails.printable === 'false') {
        alert('This invoice must be approved before payment can be processed.');
        return;
      }

      // Close bill generated modal
      const billModal = bootstrap.Modal.getInstance(document.getElementById('billGeneratedModal'));
      if (billModal) {
        billModal.hide();
      }

      // Reset payment state
      this.paymentPanel = false;
      this.paymentMethod = true;
      this.transactionSummary = false;
      this.transactionCompleted = false;
      this.paymentMessage = null;
      this.customerPhone = this.visit.phoneNumber || '';

      // Open payment modal
      setTimeout(() => {
        this.openModal('payment-modal');
      }, 400);
    },

    // Show Payment Method Selection
    showPaymentMethod() {
      this.paymentMethod = true;
      this.paymentPanel = false;
      this.transactionSummary = false;
      this.transactionCompleted = false;
    },

    // Show Transaction Summary
    showTransactionSummary(paymentMode) {
      this.paymentData.paymentMode = paymentMode;

      if (paymentMode === "MPESA") {
        if (!this.customerPhone) {
          alert("Enter phone number");
          return;
        }
      }
      if (paymentMode === "BANK") {
        if (!this.paymentData.bankReceiptNo) {
          alert("Enter Bank Receipt No");
          return;
        }
      }

      this.paymentMethod = false;
      this.paymentPanel = false;
      this.transactionSummary = true;
      this.transactionCompleted = false;
    },

    // Make Payment
    makePayment() {
      if (this.paymentData.paymentMode === "MPESA") {
        if (!this.customerPhone) {
          alert("Enter phone number");
          return;
        }
      }
      if (this.paymentData.paymentMode === "BANK") {
        if (!this.paymentData.bankReceiptNo) {
          alert("Enter Bank Receipt No");
          return;
        }
      }

      this.paymentMessage = "Payment Request sent";
      this.paymentData.phoneNumber = this.customerPhone;

      const data = new FormData();
      data.append("function", "makePayment");
      data.append("paymentData", JSON.stringify(this.paymentData));

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.paymentMethod = false;
              this.paymentPanel = false;
              this.startPaymentCheck();
            } else {
              alert(res.data.message);
            }
          })
          .catch((e) => {
            alert(e.message);
          });
    },

    // Start Payment Check Timer
    startPaymentCheck() {
      this.stopPaymentCheck();
      this.paymentCheckStartTime = Date.now();
      this.isCheckingPayment = true;

      this.timer = setInterval(() => {
        this.checkPayment();
      }, 3000);
    },

    // Stop Payment Check Timer
    stopPaymentCheck() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
      this.isCheckingPayment = false;
      this.paymentCheckStartTime = null;
    },

    // Check Payment Status
    checkPayment() {
      const currentTime = Date.now();
      const elapsedTime = currentTime - this.paymentCheckStartTime;

      if (elapsedTime >= this.paymentTimeoutDuration) {
        this.handlePaymentTimeout();
        return;
      }

      const remainingTime = Math.ceil((this.paymentTimeoutDuration - elapsedTime) / 1000);
      this.paymentMessage = `Waiting for payment... (${remainingTime}s remaining)`;

      const data = new FormData();
      data.append("function", "checkPayment");
      data.append("accountRef", this.paymentData.accNo);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.handlePaymentSuccess(res);
            }
          })
          .catch((e) => {
            console.error("Payment check error:", e.message);
          });
    },

    // Handle Payment Success
    handlePaymentSuccess(res) {
      this.paymentMessage = null;
      this.stopPaymentCheck();
      this.paymentData.success = true;
      this.receiptDetails = res.data.data.receipt.receiptDetails;

      this.transactionSummary = false;
      this.transactionCompleted = true;

      // Reload existing charges to reflect payment
      this.loadExistingCharges();
    },

    // Handle Payment Timeout
    handlePaymentTimeout() {
      this.stopPaymentCheck();
      this.paymentMessage = "Payment check timed out. Please check your payment status manually or try again.";
    },

    // Get Reconciled Records for Bank Payment
    getReconciledRecords(search) {
      this.alert.info = true;
      this.alert.success = false;
      this.alert.failed = false;
      this.paymentMessage = `Searching for ${search}...`;

      const data = new FormData();
      data.append("function", "getReconciledRecords");
      data.append("page", 1);
      data.append("rows_per_page", 10);
      data.append("search", search);
      data.append("preciseSearch", true);

      execute(data, biller)
          .then((res) => {
            this.paymentMessage = res.data.message;
            this.alert.info = false;
            if (res.data.success) {
              this.alert.success = true;
              this.ReconTransactions = res.data.data.ReconTransactions;
              if (this.ReconTransactions.length > 0) {
                this.paymentMessage = `${this.ReconTransactions[0].description} | KES ${this.ReconTransactions[0].amount} | ${this.ReconTransactions[0].date} | ${this.ReconTransactions[0].transactionCode}`;
              }
            } else {
              this.alert.failed = true;
            }
          })
          .catch((e) => {
            this.paymentMessage = e.message;
            this.alert.failed = true;
          });
    },

    // Print Receipt
    printReceipt() {
      if (!this.receiptDetails.receiptNo) {
        alert("Receipt not available. Try again later.");
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: this.receiptDetails.receiptNo
      });

      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    },

    // Open Modal Helper
    openModal(modalId) {
      const modalEl = document.getElementById(modalId);
      if (!modalEl) {
        console.error(`Modal with id "${modalId}" not found`);
        return;
      }

      if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const modal = new bootstrap.Modal(modalEl);
        modal.show();
        return;
      }

      // Fallback
      modalEl.classList.add('show');
      modalEl.style.display = 'block';
      document.body.classList.add('modal-open');
    },

    // Close Modal Helper
    closeModal(modalId) {
      const modalEl = document.getElementById(modalId);
      if (!modalEl) return;

      if (typeof bootstrap !== 'undefined' && bootstrap.Modal) {
        const modalInstance = bootstrap.Modal.getInstance(modalEl);
        if (modalInstance) {
          modalInstance.hide();
        }
        return;
      }

      // Fallback
      modalEl.classList.remove('show');
      modalEl.style.display = 'none';
      document.body.classList.remove('modal-open');
    }
  },
  mounted() {
    this.visitID = this.$route.params.id;
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';
    this.zone = sessionStorage.getItem("sessionZone") || '';

    if (this.visitID) {
      this.loadVisit();
      this.loadExistingCharges();
      this.getIncomeTypes();
      this.getSubCounties();
    } else {
      this.loading = false;
      this.showMessage('No visit specified', 'error');
    }
  }
};
</script>

<style scoped>
.font-14px { font-size: 14px; }
.font-size-13 { font-size: 13px; }

.card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
  border-radius: 0.5rem;
}

.table > :not(caption) > * > * {
  padding: 0.6rem 0.75rem;
}

.btn-label {
  position: relative;
  padding-left: 44px;
}

.btn-label .label-icon {
  position: absolute;
  width: 32px;
  left: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(255,255,255,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 1px solid rgba(255,255,255,0.1);
}

code {
  padding: 0.2rem 0.4rem;
  font-size: 90%;
  background-color: #f8f9fa;
  border-radius: 0.25rem;
  color: #333;
}
</style>